import { fetchWithTimeout, handleFetchResponse } from '@noths/polaris-client-utils';
import { Navigation } from '@noths/polaris-dev-ts-types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import getConfig from 'next/config';
import qs, { ParsedUrlQueryInput } from 'querystring';

import { NextConfig, Product } from 'src/types';
import { ReduxApplicationState } from '../combinedReducer';

const { publicRuntimeConfig } = getConfig() as NextConfig;
const CATALOGUE_API_ENDPOINT = publicRuntimeConfig.endpoints.catalogueMesh;
const SERVER_SIDE_TIMEOUT = 4000;

export interface ProductDetailsAPIResponse {
  product: Product;
  navigation: Navigation[];
  metaDescription: string;
}

export interface RequestProductsOptions {
  query: ParsedUrlQueryInput;
  cookie?: string;
  isHealthcheckRequest?: boolean;
}

interface ProductsAPIHandledRejection {
  redirectUrl?: string;
}

export const requestProductDetails = createAsyncThunk<
  ProductDetailsAPIResponse,
  RequestProductsOptions,
  { rejectValue: ProductsAPIHandledRejection }
>(
  'product/requestProductDetails',
  async ({ query, cookie, isHealthcheckRequest }, { getState, rejectWithValue }) => {
    if (isHealthcheckRequest) {
      // Dynamically import json file to prevent it being included on the client. Healthcheck request only happens server side.
      const { product, navigation, metaDescription } = await import('./mocks/productDetails.json');

      return {
        product,
        navigation,
        metaDescription,
      } as unknown as ProductDetailsAPIResponse;
    }

    const {
      userConfiguration: { currencyCode, deliveryZone },
    } = getState() as ReduxApplicationState;

    const querystring = qs.encode({
      ...query,
      ...(currencyCode ? { currencyCode } : {}),
      ...(deliveryZone ? { deliveryZone } : {}),
      ...(query.previewing ? { previewing: true } : {}),
    });

    const headers = {
      ...(query.previewing && cookie
        ? {
            headers: {
              Cookie: cookie,
            },
          }
        : {}),
    };

    const apiResponse = (await fetchWithTimeout({
      apiEndpoint: CATALOGUE_API_ENDPOINT,
      apiPath: 'v1/product-details/',
      querystring,
      timeout: SERVER_SIDE_TIMEOUT,
      useTimeout: true,
      ...(headers && headers),
    })) as Response;

    if (apiResponse?.status === 301 && apiResponse?.headers.get('location')) {
      const redirectUrl = new URL(apiResponse.headers.get('location') || '');
      redirectUrl.searchParams.append('redirectedFrom', 'pdp');

      return rejectWithValue({
        redirectUrl: `${redirectUrl.pathname}${redirectUrl.search}`,
      });
    }

    const { product, navigation, metaDescription }: ProductDetailsAPIResponse =
      await handleFetchResponse(apiResponse);

    return {
      product,
      navigation,
      metaDescription,
    };
  },
);
